import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { UserTrophy } from '../types/trophy';

export function useTrophies(userId: string | undefined) {
  const [trophies, setTrophies] = useState<UserTrophy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchTrophies = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('user_trophies')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Map trophy types to titles and descriptions
        const processedTrophies = (data || []).map(trophy => ({
          ...trophy,
          ...getTrophyDetails(trophy.type)
        }));

        setTrophies(processedTrophies);
      } catch (err) {
        console.error('Error fetching trophies:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch trophies'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrophies();
  }, [userId]);

  return { trophies, isLoading, error };
}

function getTrophyDetails(type: string): { title: string; description: string } {
  const trophyTypes = {
    first_submission: {
      title: 'First Submission',
      description: 'Submitted your first animatronic data'
    },
    ten_submissions: {
      title: 'Data Contributor',
      description: 'Submitted 10 animatronic entries'
    },
    first_comment: {
      title: 'First Comment',
      description: 'Posted your first comment in the forum'
    },
    popular_submission: {
      title: 'Popular Submission',
      description: 'One of your submissions reached 100 views'
    },
    helpful_contributor: {
      title: 'Helpful Contributor',
      description: 'Received 10 helpful votes on your comments'
    }
  };

  return trophyTypes[type as keyof typeof trophyTypes] || {
    title: 'Unknown Trophy',
    description: 'Trophy details not found'
  };
}